import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useApp, CURRENCIES } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { LANGS } from "@/i18n/translations";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Menu, ChevronDown, Droplet, Globe, Coins, LayoutDashboard, LogOut, User as UserIcon } from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";

const NAV = [
  { key: "nav.home", to: "/" },
  { key: "nav.prices", to: "/prix" },
  { key: "nav.directory", to: "/annuaire" },
  { key: "nav.tools", to: "/outils" },
  { key: "nav.blog", to: "/blog" },
  { key: "nav.faq", to: "/faq" },
  { key: "nav.contact", to: "/contact" },
];

const MARKET_LINKS = [
  { key: "nav.market.bulk", to: "/marche/vrac" },
  { key: "nav.market.packaged", to: "/marche/conditionne" },
  { key: "nav.market.requests", to: "/marche/demandes" },
];

export const Header = () => {
  const { t, lang, setLang, currency, setCurrency } = useApp();
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const currentLang = LANGS.find((l) => l.code === lang);
  const isAdmin = user?.role === "admin";

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-primary ${
      isActive ? "text-primary" : "text-neutral-700"
    }`;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full border-b border-border/60 bg-white/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        {/* Brand */}
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Droplet className="h-5 w-5" fill="currentColor" />
          </span>
          <span className="font-serif text-2xl font-bold tracking-tight text-primary leading-none">
            Evoo<span className="text-accent">Tunisia</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 lg:flex">
          <NavLink to="/" className={linkClass} data-testid="nav-home">{t("nav.home")}</NavLink>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-market-trigger"
              className="flex items-center gap-1 text-sm font-medium text-neutral-700 outline-none transition-colors hover:text-primary"
            >
              {t("nav.market")} <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-white">
              {MARKET_LINKS.map((m) => (
                <DropdownMenuItem key={m.to} asChild>
                  <Link to={m.to} data-testid={`nav-${m.to.replace(/\//g, "-")}`}>{t(m.key)}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {NAV.slice(1).map((n) => (
            <NavLink key={n.to} to={n.to} className={linkClass} data-testid={`nav-${n.to.slice(1)}`}>
              {t(n.key)}
            </NavLink>
          ))}
        </nav>

        {/* Right controls */}
        <div className="flex items-center gap-2">
          {/* Currency */}
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger
              data-testid="currency-selector"
              className="hidden h-9 w-[86px] border-border sm:flex"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {Object.keys(CURRENCIES).map((c) => (
                <SelectItem key={c} value={c} data-testid={`currency-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Language */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="language-selector"
              className="flex h-9 items-center gap-1.5 rounded-md border border-border px-2.5 text-sm font-medium text-neutral-700 outline-none transition-colors hover:border-primary hover:text-primary"
            >
              <Globe className="h-4 w-4" />
              <span className="hidden sm:inline">{currentLang?.label}</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={lang === l.code ? "font-semibold text-primary" : ""}
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user && <NotificationBell />}

          {user ? (
            <div className="hidden items-center gap-2 md:flex">
              {company?.verified && <VerifiedBadge />}
              <DropdownMenu>
                <DropdownMenuTrigger
                  data-testid="user-menu-trigger"
                  className="flex h-9 items-center gap-2 rounded-md border border-border px-2.5 text-sm font-medium text-neutral-700 outline-none transition-colors hover:border-primary hover:text-primary"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <UserIcon className="h-3.5 w-3.5" />
                  </span>
                  <span className="max-w-[120px] truncate">{user.name}</span>
                  <ChevronDown className="h-3.5 w-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white">
                  {isAdmin ? (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard/admin" data-testid="menu-admin"><LayoutDashboard className="me-2 h-4 w-4" /> {t("dash.menu.admin")}</Link>
                    </DropdownMenuItem>
                  ) : (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" data-testid="menu-dashboard"><LayoutDashboard className="me-2 h-4 w-4" /> {t("nav.dashboard")}</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard/entreprise"><UserIcon className="me-2 h-4 w-4" /> {t("dash.menu.company")}</Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-destructive focus:text-destructive">
                    <LogOut className="me-2 h-4 w-4" /> {t("auth.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="hidden items-center gap-2 md:flex">
              <Link to="/connexion" data-testid="header-login">
                <Button variant="ghost" className="h-9">{t("nav.login")}</Button>
              </Link>
              <Link to="/inscription" data-testid="header-register">
                <Button className="h-9 bg-primary text-primary-foreground hover:bg-primary/90">
                  {t("nav.register")}
                </Button>
              </Link>
            </div>
          )}

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9 lg:hidden" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              data-testid="mobile-drawer"
              className="flex w-[300px] max-w-[85vw] flex-col gap-0 overflow-y-auto bg-white p-0"
            >
              <SheetHeader className="shrink-0 border-b border-border p-4 text-start">
                <SheetTitle className="flex items-center gap-2 font-serif text-xl font-bold text-primary">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
                    <Droplet className="h-4 w-4" fill="currentColor" />
                  </span>
                  Evoo<span className="text-accent">Tunisia</span>
                </SheetTitle>
              </SheetHeader>

              <div className="flex flex-1 flex-col gap-1 p-4">
                {/* Main nav */}
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                    className={({ isActive }) =>
                      `rounded-md px-3 py-2.5 text-base font-medium hover:bg-secondary ${isActive ? "bg-secondary text-primary" : "text-neutral-800"}`}
                  >
                    {t(n.key)}
                  </NavLink>
                ))}

                {/* Market */}
                <div className="mt-2 border-t border-border pt-2">
                  <p className="px-3 py-1 text-xs font-semibold uppercase text-neutral-400">{t("nav.market")}</p>
                  {MARKET_LINKS.map((m) => (
                    <NavLink
                      key={m.to}
                      to={m.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav${m.to.replace(/\//g, "-")}`}
                      className="block rounded-md px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-secondary"
                    >
                      {t(m.key)}
                    </NavLink>
                  ))}
                </div>

                {/* Language */}
                <div className="mt-2 border-t border-border pt-3">
                  <p className="mb-1.5 flex items-center gap-1.5 px-3 text-xs font-semibold uppercase text-neutral-400">
                    <Globe className="h-3.5 w-3.5" /> {t("nav.language")}
                  </p>
                  <div className="grid grid-cols-2 gap-1.5 px-1">
                    {LANGS.map((l) => (
                      <button
                        key={l.code}
                        type="button"
                        data-testid={`mobile-lang-${l.code}`}
                        onClick={() => setLang(l.code)}
                        className={`rounded-md border px-2 py-1.5 text-sm font-medium transition-colors ${
                          lang === l.code ? "border-primary bg-primary/10 text-primary" : "border-border text-neutral-700 hover:border-primary/50"
                        }`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Currency */}
                <div className="mt-3 border-t border-border pt-3">
                  <p className="mb-1.5 flex items-center gap-1.5 px-3 text-xs font-semibold uppercase text-neutral-400">
                    <Coins className="h-3.5 w-3.5" /> {t("nav.currency")}
                  </p>
                  <div className="grid grid-cols-3 gap-1.5 px-1">
                    {Object.keys(CURRENCIES).map((c) => (
                      <button
                        key={c}
                        type="button"
                        data-testid={`mobile-currency-${c}`}
                        onClick={() => setCurrency(c)}
                        className={`rounded-md border px-2 py-1.5 text-sm font-medium transition-colors ${
                          currency === c ? "border-primary bg-primary/10 text-primary" : "border-border text-neutral-700 hover:border-primary/50"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Account / auth */}
                <div className="mt-4 flex flex-col gap-2 border-t border-border pt-4">
                  {user ? (
                    <>
                      <div className="flex items-center gap-2 px-1 pb-1">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <UserIcon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-neutral-800">{user.name}</span>
                        {company?.verified && <VerifiedBadge />}
                      </div>
                      <Link to={isAdmin ? "/dashboard/admin" : "/dashboard"} onClick={() => setOpen(false)} data-testid="mobile-dashboard">
                        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                          <LayoutDashboard className="me-2 h-4 w-4" /> {isAdmin ? t("dash.menu.admin") : t("nav.dashboard")}
                        </Button>
                      </Link>
                      <Button variant="outline" className="w-full" onClick={handleLogout} data-testid="mobile-logout">
                        <LogOut className="me-2 h-4 w-4" /> {t("auth.logout")}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Link to="/connexion" onClick={() => setOpen(false)} data-testid="mobile-login">
                        <Button variant="outline" className="w-full">{t("nav.login")}</Button>
                      </Link>
                      <Link to="/inscription" onClick={() => setOpen(false)} data-testid="mobile-register">
                        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                          {t("nav.register")}
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
